/**
 * Sub-hook for PO Lines workflow actions: hold, release, accept, decline, critical toggle.
 */
import { useState, useCallback } from 'react';
import { toast } from 'sonner';
import { purchasingAPI, PurchaseOrderLine } from '../../../../lib/api';
import { getApiErrorMessage } from '../../../../lib/api/types/common';
import { useAuth } from '../../../../contexts/AuthContext';
import { useMenuSettings } from '../../../../contexts/MenuSettingsContext';
import { useUserPermissions } from '../../../../contexts/UserPermissionsContext';
import { useView } from '../../../../contexts/ViewContext';
import { NextActionResult } from './types';

interface UsePOLinesActionsParams {
  fetchLines: () => Promise<void>;
  setLines: React.Dispatch<React.SetStateAction<PurchaseOrderLine[]>>;
  bustCacheRef: React.MutableRefObject<boolean>;
}

export function usePOLinesActions({ fetchLines, setLines, bustCacheRef }: UsePOLinesActionsParams) {
  const { user } = useAuth();
  const { isFeatureEnabled } = useMenuSettings();
  const { hasPurchasingWriteAccess } = useUserPermissions();
  // CR-4647: Use portalMode for role detection so dual-mode users who toggle
  // to customer view get customer actions, not just raw company_type users.
  const { portalMode } = useView();

  // Hold state
  const [selectedLineForHold, setSelectedLineForHold] = useState<PurchaseOrderLine | null>(null);
  const [putOnHoldModalOpen, setPutOnHoldModalOpen] = useState(false);
  const [releaseHoldModalOpen, setReleaseHoldModalOpen] = useState(false);
  const [holdReason, setHoldReason] = useState('');

  // Workflow state
  const [selectedLineForWorkflow, setSelectedLineForWorkflow] = useState<PurchaseOrderLine | null>(null);
  const [acceptModalOpen, setAcceptModalOpen] = useState(false);
  const [declineModalOpen, setDeclineModalOpen] = useState(false);

  // Hold handlers
  const handlePutOnHold = useCallback((line: PurchaseOrderLine) => {
    setSelectedLineForHold(line);
    setHoldReason('');
    setPutOnHoldModalOpen(true);
  }, []);

  const handleReleaseHold = useCallback((line: PurchaseOrderLine) => {
    setSelectedLineForHold(line);
    setReleaseHoldModalOpen(true);
  }, []);

  const submitPutOnHold = useCallback(async () => {
    if (!selectedLineForHold || !holdReason.trim()) {
      toast.error('Please enter a reason for putting this line on hold');
      return;
    }

    try {
      const response = await purchasingAPI.putLineOnHold(selectedLineForHold.id, {
        reason: holdReason
      });

      if (response.data.status === 'success') {
        toast.success(response.data.message);
        setPutOnHoldModalOpen(false);
        setSelectedLineForHold(null);
        setHoldReason('');
        bustCacheRef.current = true;
        fetchLines();
      }
    } catch (error: unknown) {
      console.error('Failed to put line on hold:', error);
      toast.error(getApiErrorMessage(error, 'Failed to put line on hold'));
    }
  }, [selectedLineForHold, holdReason, fetchLines, bustCacheRef]);

  const submitReleaseHold = useCallback(async () => {
    if (!selectedLineForHold) return;

    try {
      const response = await purchasingAPI.releaseLineHold(selectedLineForHold.id);

      if (response.data.status === 'success') {
        toast.success(response.data.message);
        setReleaseHoldModalOpen(false);
        setSelectedLineForHold(null);
        bustCacheRef.current = true;
        fetchLines();
      }
    } catch (error: unknown) {
      console.error('Failed to release hold:', error);
      toast.error(getApiErrorMessage(error, 'Failed to release hold'));
    }
  }, [selectedLineForHold, fetchLines, bustCacheRef]);

  // Toggle critical flag (CR-3404)
  const handleToggleCritical = useCallback(async (line: PurchaseOrderLine) => {
    try {
      const newCriticalState = !line.is_critical;
      const response = await purchasingAPI.setLineCritical(line.id, newCriticalState);

      if (response.data.status === 'success') {
        toast.success(response.data.message);
        setLines(prevLines =>
          prevLines.map(l =>
            l.id === line.id ? { ...l, is_critical: newCriticalState } : l
          )
        );
      }
    } catch (error: unknown) {
      console.error('Failed to toggle critical:', error);
      toast.error(getApiErrorMessage(error, 'Failed to update critical status'));
    }
  }, [setLines]);

  // Workflow action handler
  const handleWorkflowAction = useCallback(async (data: { action: string; lineId: number; comments?: string; reason?: string; proposedDate?: string; proposedQuantity?: number; proposedPrice?: number }) => {
    try {
      let response;

      switch (data.action) {
        case 'accept':
          response = await purchasingAPI.acceptDeliveryDate(data.lineId, {
            comments: data.comments,
          });
          break;
        case 'decline':
          response = await purchasingAPI.declineDeliveryDate(data.lineId, {
            reason: data.reason ?? '',
            comments: data.comments,
          });
          break;
        default:
          throw new Error(`Unknown action: ${data.action}`);
      }

      if (response?.data?.message) {
        toast.success(response.data.message);
      }

      setAcceptModalOpen(false);
      setDeclineModalOpen(false);
      setSelectedLineForWorkflow(null);
      bustCacheRef.current = true;
      fetchLines();
    } catch (error: unknown) {
      console.error('Workflow action failed:', error);
      toast.error(getApiErrorMessage(error, 'Workflow action failed'));
      throw error;
    }
  }, [fetchLines, bustCacheRef]);

  // Determine if current user should see Accept/Reject action buttons
  // CR-4203: Now also checks role-based site permissions
  const shouldShowActions = useCallback((line: PurchaseOrderLine): boolean => {
    if (!user || !user.platform) return false;
    // CR-4697: No actions on closed (fully received) PO lines
    if (line.status === 'received') return false;
    // Schedule-aware tenants: hide PO-line Accept/Decline — negotiation
    // lives on the Schedule modal only (otherwise two disconnected audit
    // trails for the same logical action).
    if (isFeatureEnabled('po_line_schedules')) return false;

    const status = line.acknowledge_status;
    const isCustomer = portalMode === 'customer' || user.platform === 'CUSTOMER';
    const isSupplier = !isCustomer && (user.platform === 'SUPPLIER' || user.platform === 'HELIX');

    // CR-4647: Site-based write access only applies to supplier users.
    // Customers always have write access to their own PO lines.
    if (isSupplier && !hasPurchasingWriteAccess(line.supplier_id)) return false;

    if (isSupplier) {
      switch (status) {
        case 'change_proposed_customer':
          return true;
        case 'change_declined':
          if (line.previous_acknowledge_status === 'change_proposed_customer') {
            return true;
          }
          return false;
        default:
          return false;
      }
    }

    if (isCustomer) {
      switch (status) {
        case 'new':
          return true;
        case 'change_proposed_supplier':
          return true;
        case 'change_declined':
          if (line.previous_acknowledge_status === 'change_proposed_supplier') {
            return true;
          }
          return false;
        default:
          return false;
      }
    }

    return false;
  }, [user, portalMode, hasPurchasingWriteAccess, isFeatureEnabled]);

  // Determine if decline button should be shown for a line.
  const shouldShowDecline = useCallback((line: PurchaseOrderLine): boolean => {
    if (!shouldShowActions(line)) return false;

    const isCustomerDecline = portalMode === 'customer' || user?.platform === 'CUSTOMER';
    if (isCustomerDecline && line.acknowledge_status === 'new') {
      return isFeatureEnabled('allow_customer_decline');
    }

    return true;
  }, [user, portalMode, shouldShowActions, isFeatureEnabled]);

  // CR-4203: Now also checks role-based site permissions
  const shouldShowProposeChange = useCallback((line: PurchaseOrderLine): boolean => {
    if (!user || !user.platform) return false;
    // CR-4697: No actions on closed (fully received) PO lines
    if (line.status === 'received') return false;
    // Schedule-aware tenants: hide PO-line Propose — negotiation
    // lives on the Schedule modal only.
    if (isFeatureEnabled('po_line_schedules')) return false;

    const status = line.acknowledge_status;
    const isCustomer = portalMode === 'customer' || user.platform === 'CUSTOMER';
    const isSupplier = !isCustomer && (user.platform === 'SUPPLIER' || user.platform === 'HELIX');

    // CR-4647: Site-based write access only applies to supplier users.
    if (isSupplier && !hasPurchasingWriteAccess(line.supplier_id)) return false;

    if (isSupplier) {
      switch (status) {
        case 'new':
        case 'accepted':
        case 'wip':
          return true;
        case 'change_declined':
          if (line.previous_acknowledge_status === 'change_proposed_supplier') {
            return true;
          }
          return false;
        default:
          return false;
      }
    }

    if (isCustomer) {
      switch (status) {
        case 'new':
        case 'accepted':
        case 'wip':
          return true;
        case 'change_declined':
          if (line.previous_acknowledge_status === 'change_proposed_customer') {
            return true;
          }
          return false;
        default:
          return false;
      }
    }

    return false;
  }, [user, portalMode, hasPurchasingWriteAccess, isFeatureEnabled]);

  // Get next action for display (CR-3410)
  const getNextAction = useCallback((line: PurchaseOrderLine): NextActionResult => {
    // CR-4696: Closed lines need no further action
    if (line.status === 'received') {
      return { text: '-', className: 'text-muted-foreground' };
    }
    if (line.on_hold) {
      if (line.on_hold_by_company_type === 'supplier' || line.on_hold_by_company_type === 'helix') {
        return { text: 'Waiting Customer', className: 'bg-blue-100 text-blue-700' };
      } else if (line.on_hold_by_company_type === 'customer') {
        return { text: 'Waiting Supplier', className: 'bg-orange-100 text-orange-700' };
      }
      return { text: 'On Hold', className: 'bg-gray-100 text-gray-700' };
    }

    if (line.status === 'cancelled') {
      return { text: '-', className: 'text-muted-foreground' };
    }

    if (line.waiting_on === 'Supplier') {
      return { text: 'Waiting Customer', className: 'bg-blue-100 text-blue-700' };
    } else if (line.waiting_on === 'Customer') {
      return { text: 'Waiting Supplier', className: 'bg-orange-100 text-orange-700' };
    }

    return { text: '-', className: 'text-muted-foreground' };
  }, []);

  return {
    // Hold state
    selectedLineForHold,
    setSelectedLineForHold,
    putOnHoldModalOpen,
    setPutOnHoldModalOpen,
    releaseHoldModalOpen,
    setReleaseHoldModalOpen,
    holdReason,
    setHoldReason,

    // Workflow state
    selectedLineForWorkflow,
    setSelectedLineForWorkflow,
    acceptModalOpen,
    setAcceptModalOpen,
    declineModalOpen,
    setDeclineModalOpen,

    // Handlers
    handlePutOnHold,
    handleReleaseHold,
    submitPutOnHold,
    submitReleaseHold,
    handleToggleCritical,
    handleWorkflowAction,
    shouldShowActions,
    shouldShowDecline,
    shouldShowProposeChange,
    getNextAction,
  };
}
